import React, { useEffect } from "react";
import { useAppSelector, selectors, isaacApi } from "../../state";
import { Link } from "react-router-dom";
import { Button, Col, Container, Row } from "reactstrap";
import { SITE_SUBJECT_TITLE } from "../../services";
import { NewsCarousel } from "../elements/NewsCarousel";
import { EventsCarousel } from "../elements/EventsCarousel";
import classNames from "classnames";
import { Dashboard } from "../elements/Dashboard";
import { CareersBanner } from "../elements/CareersBanner";
import Resources from "../elements/Resources";
import TestimonialComment from "../elements/TestimonialComment";

export const Homepage = () => {
  useEffect(() => {
    document.title = "Isaac " + SITE_SUBJECT_TITLE;
  }, []);

  const user = useAppSelector(selectors.user.orNull);

  const { data: news } = isaacApi.endpoints.getNewsPodList.useQuery({
    subject: "news",
    orderDescending: true,
  });

  const { data: promo } = isaacApi.endpoints.getNewsPodList.useQuery({
    subject: "promo",
    orderDescending: true,
  });

  const [teacherPromoItem] = promo
    ? [
        promo.find((item) => item && !item?.id?.includes("public")),
        promo.find((item) => item && item?.id?.includes("public")),
      ]
    : [undefined, undefined];

  const featuredNewsItem = news && user?.loggedIn ? news[0] : undefined;

  const carouselNewsItems = news;

  return (
    <>
      <div id="homepage">
        <section id="call-to-action" className={`homepageHero ${user?.loggedIn ? "pb-lg-4" : ""}`}>
          <Container className="pt-4 z1 pb-4">
            <Dashboard promoItem={teacherPromoItem} featuredNewsItem={featuredNewsItem} />
          </Container>
        </section>

        <section id="why-choose-us" className="bg-white">
          <div className="why-choose-us-section pt-5 pb-4">
            <Container className="pt-4 pb-5">
              <h1 className="primary-heading mb-4">Why choose us</h1>
              <Row className="pt-4 mb-2 why-choose-us-row">
                <Col lg={6} xs={12} className="pb-3 pb-lg-0">
                  <img
                    src="/assets/why-choose-us.png"
                    alt="A teacher sat with students in a library, talking about content in a textbook in front of them."
                    className="img-fluid d-block"
                  />
                </Col>
                <Col lg={6} xs={12} className="why-choose-us-copy">
                  <p className="body-text why-choose-us-intro">Why should you choose Isaac Computer Science:</p>
                  <ul className="body-text mt-0 why-choose-us-list">
                    <li>Free computer science learning resources backed by experts</li>
                    <li>Teachers save 2.8 hours a week on teaching and lesson planning when using Isaac</li>
                    <li>Smooth progression for GCSE students to A level computer science</li>
                    <li>Connect computer science to real-world application and careers</li>
                    <li>Funded by the Department for Education</li>
                  </ul>
                  <div className="why-choose-us-buttons">
                    <Button
                      size="lg"
                      tag={Link}
                      to="/pages/teacher_landing_page"
                      target="_blank"
                      className="primary-button text-light"
                      block
                    >
                      Benefits for teachers
                    </Button>
                    <Button
                      size="lg"
                      tag={Link}
                      to="/pages/student_landing_page"
                      target="_blank"
                      className="primary-button text-light"
                      block
                    >
                      Benefits for students
                    </Button>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </section>

        {!user?.loggedIn && (
          <section id="events" className="event-section">
            <div className="event-section-background-img">
              <Container className="pt-4 pb-5">
                <div className="eventList pt-5 pattern-03">
                  <h2 className="homepage-sub-title text-left mb-4">Events</h2>
                  <p className="pt-4 pb-2 event-text-description text-left">
                    {
                      "We offer free online events for students. Choose from our booster events to support revision and improve understanding of classroom learning, or our discovery events, to explore the career possibilities that computer science opens up."
                    }
                  </p>
                  <EventsCarousel />
                  <div className="center-container">
                    <Link className="browse-events" to="/events">
                      Browse all events
                    </Link>
                  </div>
                </div>
              </Container>
            </div>
          </section>
        )}

        {!user?.loggedIn && (
          <section id="resources" className="resources-section">
            <Container className="pt-4 pb-5">
              <div className="pt-5">
                <h2 className="homepage-sub-title text-left mb-4">Resources</h2>
              </div>
              <Row>
                <div className="resources-columns-container">
                  <Col className="resources-custom-col-60">
                    <Resources
                      heading="Computer science topics"
                      description="Discover over 50 free computer science topics and over 1000 questions, mapped to the leading exam specifications in England. Review key curriculum areas and get specific feedback to address common misconceptions."
                      links={[
                        { to: "/topics/gcse", text: "GCSE topics" },
                        { to: "/topics/a_level", text: "A level topics" },
                      ]}
                    />
                  </Col>
                  <Col className="resources-custom-col-30">
                    <Resources
                      heading="Question finder"
                      description="Specify your search criteria and we’ll generate a random selection of up to 10 questions!"
                      links={[{ to: "/gameboards/new", text: "Question finder" }]}
                      tooltip={
                        <div className="resources-tooltip-container">
                          <img
                            src="/assets/q-finder.svg"
                            alt="Question Finder"
                            className="q-finder-image d-none d-md-block"
                          />
                          <span className="tooltip-text p-3">
                            You can build a gameboard by selecting the areas of interest, stage and difficulties.
                            <br />
                            You can select more than one entry in each area.
                          </span>
                        </div>
                      }
                    />
                  </Col>
                </div>
              </Row>
              <br />
              <TestimonialComment
                imageSrc="/assets/star.svg"
                altText="Star"
                text="According to the 2024 annual survey, Isaac Computer Science can save teachers three hours of work per week."
              />
            </Container>
          </section>
        )}

        <section id="careers" className="career-section">
          <CareersBanner />
        </section>

        <section id="news">
          <Container
            className={classNames("pt-4 pb-2", {
              "mt-3 pt-lg-0": user?.loggedIn ?? false,
            })}
          >
            <div className="eventList pt-3 pt-md-4 pattern-03-reverse">
              <h2 className="homepage-sub-title mb-4 pt-lg-3">News</h2>
              <NewsCarousel items={carouselNewsItems} />
            </div>
          </Container>
        </section>

        {user?.loggedIn && (
          <section id="events">
            <Container className="pt-4 pb-5">
              <div className="eventList pattern-03">
                <h2 className="h-title text-center mb-4">Events</h2>
                <p className="pt-4 pb-2 event-description text-center col-md-8 offset-md-2">
                  {"We offer free online events for students. Visit our "}
                  <Link to="/events">Events page</Link>
                  {" to see what’s happening, and sign up today!"}
                </p>
                <EventsCarousel />
                <Link to="/events">See all Events</Link>
              </div>
            </Container>
          </section>
        )}
      </div>
    </>
  );
};
